import type { RouteLocationNormalized, RouteLocationRaw } from 'vue-router'

// A fresh tab opened by the web service worker from a tapped notification has
// nothing but its URL to carry the notification's tenant, so the worker appends
// `?org=<public id>`. The console router consumes it on the navigation that
// carries it — in practice the first one, at boot — after the user's
// organizations are loaded: select the organization if it is one of theirs,
// then re-enter the same route without the key. An organization that is not
// the user's is ignored, like a path that matches no route — the link still
// opens, under the active organization, and the Silent Wall answers.
//
// The organization is written BEFORE the navigation commits, the reverse of
// useOrganizationSwitch's rule. Legal here because a fresh tab has nothing
// tenant-bound mounted yet; the rule exists to protect a mounted page.
//
// Console only: the native field app has no service worker, so `?org=` never
// reaches its router. Internal to push. In-app navigations never carry `org`.
export const TENANT_QUERY_KEY = 'org'

interface TenantStore {
  organizations: ReadonlyArray<{ id: string }>
  currentOrganizationId: string | null
  setCurrentOrganization: (id: string | null) => void
}

type Location = Pick<RouteLocationNormalized, 'path' | 'query' | 'hash'>

export function consumeTenantQuery(to: Location, store: TenantStore): RouteLocationRaw | undefined {
  const wanted = [ to.query[TENANT_QUERY_KEY] ].flat()[0]
  if (wanted === undefined) return undefined

  if (typeof wanted === 'string' && wanted !== store.currentOrganizationId && store.organizations.some(o => o.id === wanted)) {
    store.setCurrentOrganization(wanted)
  }
  const query = Object.fromEntries(Object.entries(to.query).filter(([ key ]) => key !== TENANT_QUERY_KEY))
  return { path: to.path, query, hash: to.hash, replace: true }
}
